import React, { useMemo, useCallback, useState, useEffect } from "react";
import { GameBoard } from "./components/game-board.tsx";
import { GameControls } from "./components/game-controls.tsx";
import { useGame } from "./hooks/use-game.ts";
import { getDailyPuzzle, fetchDailyPuzzle, getPuzzleNumber } from "./lib/daily.ts";
import { loadStats, saveStats, recordCompletion } from "./lib/storage.ts";
import type { GameState, Puzzle } from "./types.ts";

export function App({ onWin, embedded, winExtra, difficulty }: { onWin?: (data: { stars: number; metric: number; timeMs: number }) => void; embedded?: boolean; winExtra?: React.ReactNode; difficulty?: string } = {}) {
  const [puzzle, setPuzzle] = useState<Puzzle | null>(null);
  const puzzleNumber = useMemo(() => getPuzzleNumber(), []);

  useEffect(() => {
    setPuzzle(null);
    fetchDailyPuzzle(difficulty)
      .then(setPuzzle)
      .catch(() => setPuzzle(getDailyPuzzle(difficulty)));
  }, [difficulty]);

  if (!puzzle) {
    return (
      <div className={`flex flex-col items-center justify-center ${embedded ? 'p-4 min-h-[300px]' : 'min-h-screen p-6'} max-w-[700px] mx-auto select-none`}>
        <div className="animate-pulse text-slate-500 text-sm font-mono">Loading puzzle...</div>
      </div>
    );
  }

  return <GameInner puzzle={puzzle} puzzleNumber={puzzleNumber} onWin={onWin} embedded={embedded} winExtra={winExtra} />;
}

function GameInner({ puzzle, puzzleNumber, onWin, embedded, winExtra }: { puzzle: Puzzle; puzzleNumber: number; onWin?: (data: { stars: number; metric: number; timeMs: number }) => void; embedded?: boolean; winExtra?: React.ReactNode }) {
  const { state, drop, cascadeTick, undo, reset } = useGame(puzzle);
  const [showHelp, setShowHelp] = useState(false);
  const [stats, setStats] = useState(() => loadStats());

  const won = state.phase === "won";
  const lost = state.phase === "lost";

  useEffect(() => {
    if (won && !stats.history[puzzle.id]?.completed) {
      const timeMs = (state.completedAt ?? Date.now()) - state.startedAt;
      const updated = recordCompletion(stats, {
        puzzleId: puzzle.id,
        completed: true,
        stars: state.stars,
        moves: state.moveHistory,
        timeMs,
      });
      setStats(updated);
      saveStats(updated);
      onWin?.({ stars: state.stars, metric: state.moveHistory.length, timeMs });
    }
  }, [won]);

  return (
    <div className={`flex flex-col items-center ${embedded ? 'p-4 gap-4' : 'min-h-screen p-6 gap-6'} max-w-[700px] mx-auto select-none`}>
      {/* Header — hidden when embedded */}
      {!embedded && (
        <div className="flex items-center justify-between w-full">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              <span className="text-blue-400">Spill</span>over
            </h1>
            <div className="flex gap-3 text-sm text-slate-500">
              <span>#{puzzleNumber}</span>
              {stats.streak > 0 && (
                <span className="text-blue-400">{stats.streak} day streak</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <span>Target:</span>
              <div
                className="w-5 h-5 rounded-full border-2 border-slate-600"
                style={{ backgroundColor: puzzle.targetColor ?? undefined }}
              />
            </div>
            <button
              onClick={() => setShowHelp(!showHelp)}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-800 text-slate-400 hover:text-white text-sm font-bold border border-slate-700"
            >
              ?
            </button>
          </div>
        </div>
      )}

      {/* Subheader when embedded */}
      {embedded && (
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs text-slate-500">#{puzzleNumber}</span>
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <span>Target:</span>
              <div className="w-4 h-4 rounded-full border-2 border-slate-600" style={{ backgroundColor: puzzle.targetColor ?? undefined }} />
            </div>
            {stats.streak > 0 && (
              <span className="font-mono text-xs text-[#4ECAFF]">{stats.streak} day streak</span>
            )}
          </div>
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-[#1A1D33] text-slate-400 hover:text-white text-xs font-bold border border-[#2A2D42]"
          >
            ?
          </button>
        </div>
      )}

      {/* Help */}
      {showHelp && (
        <div className="w-full bg-slate-800/80 rounded-xl p-4 text-sm text-slate-300 border border-slate-700 space-y-2">
          <p className="font-medium text-white">How to play</p>
          <p>
            Make every hex the{" "}
            <strong className="text-green-400">target color</strong> by dropping
            liquid into pools.
          </p>
          <ol className="list-decimal list-inside space-y-1">
            <li>Click any hex to drop +1 liquid into it</li>
            <li>
              When a pool exceeds its{" "}
              <span className="text-amber-400">capacity</span>, it{" "}
              <strong>overflows</strong> into all neighbors
            </li>
            <li>
              Overflow <strong>claims</strong> neighbors (changes their color)
              and adds +1
            </li>
            <li>Chain reactions happen when neighbors also overflow</li>
          </ol>
          <p className="text-slate-500">
            The number shows current count. The /N shows capacity.
          </p>
        </div>
      )}

      {/* Game board */}
      <GameBoard
        grid={state.currentGrid}
        cascadeAnimation={state.cascadeAnimation}
        onDrop={drop}
        onCascadeTick={cascadeTick}
        disabled={won || lost}
      />

      {/* Controls */}
      <GameControls
        moveCount={state.moveHistory.length}
        maxMoves={puzzle.maxMoves}
        optimalMoves={puzzle.optimalMoves}
        canUndo={state.moveHistory.length > 0 && !won && !lost}
        onUndo={undo}
        onReset={reset}
      />

      {/* Win */}
      {won && (
        <div className="w-full bg-emerald-900/40 border border-emerald-700 rounded-xl p-6 text-center space-y-3 animate-[fadeIn_0.3s_ease-out]">
          <div className="text-4xl">
            {"★".repeat(state.stars)}
            {"☆".repeat(3 - state.stars)}
          </div>
          <p className="text-xl font-bold text-emerald-300">Board Flooded!</p>
          <p className="text-sm text-slate-400">
            {state.moveHistory.length} drop
            {state.moveHistory.length !== 1 ? "s" : ""}
            {" — "}
            {state.stars === 3
              ? "Optimal!"
              : state.stars === 2
                ? "Great job!"
                : "You got it!"}
          </p>
          <ShareButton state={state} puzzleNumber={puzzleNumber} />
          {stats.totalPlayed > 0 && !embedded && (
            <div className="pt-3 border-t border-emerald-800 text-xs text-slate-500 flex justify-center gap-6">
              <span>
                Played:{" "}
                <strong className="text-slate-300">{stats.totalPlayed}</strong>
              </span>
              <span>
                Stars:{" "}
                <strong className="text-amber-400">{stats.totalStars}</strong>
              </span>
              <span>
                Best streak:{" "}
                <strong className="text-slate-300">{stats.maxStreak}</strong>
              </span>
            </div>
          )}
          {winExtra}
        </div>
      )}

      {/* Lose */}
      {lost && (
        <div className="w-full bg-red-900/30 border border-red-800 rounded-xl p-6 text-center space-y-3 animate-[fadeIn_0.3s_ease-out]">
          <p className="text-xl font-bold text-red-300">Out of drops!</p>
          <p className="text-sm text-slate-400">
            Used all {puzzle.maxMoves} drops. Try again?
          </p>
          <button
            onClick={reset}
            className="px-6 py-2 bg-red-700 hover:bg-red-600 rounded-lg text-sm font-medium transition-colors"
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}

function ShareButton({
  state,
  puzzleNumber,
}: {
  state: GameState;
  puzzleNumber: number;
}) {
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback(() => {
    const text = [
      `\uD83D\uDCA7 Spillover #${puzzleNumber}`,
      `${"★".repeat(state.stars)}${"☆".repeat(3 - state.stars)} (${state.moveHistory.length} drop${state.moveHistory.length !== 1 ? "s" : ""})`,
    ].join("\n");

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [state, puzzleNumber]);

  return (
    <button
      onClick={handleShare}
      className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-medium transition-colors"
    >
      {copied ? "Copied!" : "Share Result"}
    </button>
  );
}
